import { FunctionComponent, memo, useMemo } from "react";
import { unreachableCaseError } from "@edgetier/utilities";
import styles from "./field-factory.module.scss";
import type { IProps } from "./field-factory.types";
import { SelectEnum, SelectField } from "../../../../select";
import { DatePickerInput } from "../../../../date-picker-input";
import { Input } from "../../../../input";
import { TextInput } from "../../../../text-input";
import { Icon } from "../../../../icon";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";

/**
 * Component for generating fields based on their type in a form.
 * @param {object} props.field - The field configuration.
 * @param {boolean} props.canBeRemoved - Flag indicating if the field can be removed.
 * @param {function} props.onRemove - Function to handle field removal.
 * @returns {JSX.Element} FieldFactory component.
 */
const FieldFactory: FunctionComponent<IProps> = ({ field, canBeRemoved, activable, onRemove, variant = "default" }) => {
    const removeButton = useMemo(
        () =>
            canBeRemoved ? (
                <div className={styles["remove-button"]}>
                    {/* TODO Watchtower 2.0 - May need to switch this to an ActionIcon component */}
                    <button onClick={onRemove} type="button">
                        <Icon size="xxs" title="remove-filter" name="remove-filter" icon={faTimes} />
                    </button>
                </div>
            ) : null,
        [canBeRemoved, onRemove]
    );
    const fieldComponent = useMemo(() => {
        switch (field.type) {
            case "date-range": {
                const { type, ...otherFilterProps } = field;
                return (
                    <Input.Formik.Wrapper
                        variant={variant}
                        name={otherFilterProps.startDateName}
                        label={otherFilterProps.label}
                    >
                        {removeButton}
                        <DatePickerInput activable={activable} {...otherFilterProps} />
                    </Input.Formik.Wrapper>
                );
            }
            case "select": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <Input.Formik.Wrapper variant={variant} name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <SelectField activable={activable} icon={icon} {...otherFilterProps} />
                    </Input.Formik.Wrapper>
                );
            }
            case "select-enum": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <Input.Formik.Wrapper variant={variant} name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <SelectEnum activable={activable} icon={icon} {...otherFilterProps} />
                    </Input.Formik.Wrapper>
                );
            }
            case "search": {
                const { type, icon, fieldType, label, ...otherFilterProps } = field;
                return (
                    <Input.Formik.Wrapper variant={variant} name={otherFilterProps.name} label={label}>
                        {removeButton}
                        <TextInput.Formik activable={activable} icon={icon} type={fieldType} {...otherFilterProps} />
                    </Input.Formik.Wrapper>
                );
            }
            case "custom": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <Input.Formik.Wrapper variant={variant} name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        {field.render}
                    </Input.Formik.Wrapper>
                );
            }

            default: {
                unreachableCaseError(field);
                return null;
            }
        }
    }, [field, removeButton, activable, variant]);

    return (
        <div className={styles["field-factory"]} data-testid={`filter-${field.id}`}>
            {fieldComponent}
        </div>
    );
};

export default memo(FieldFactory);
